/**
 * Button Component
 *
 * Reusable button with Kleda styling variants.
 */

import { type ButtonHTMLAttributes, forwardRef } from 'react';
import clsx from 'clsx';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
  fullWidth?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      isLoading = false,
      fullWidth = false,
      className,
      disabled,
      children,
      ...props
    },
    ref
  ) => {
    const baseStyles =
      'inline-flex items-center justify-center font-medium rounded-kleda-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';

    const variants = {
      primary:
        'bg-kleda-primary text-white hover:bg-kleda-primary-dark focus:ring-kleda-primary',
      secondary:
        'bg-kleda-secondary text-white hover:bg-kleda-secondary-light focus:ring-kleda-secondary',
      outline:
        'border-2 border-kleda-primary text-kleda-primary hover:bg-kleda-primary hover:text-white focus:ring-kleda-primary',
      ghost:
        'text-kleda-primary hover:bg-kleda-primary/10 focus:ring-kleda-primary',
      danger:
        'bg-kleda-danger text-white hover:bg-red-700 focus:ring-kleda-danger',
    };

    const sizes = {
      sm: 'px-sm py-xs text-sm',
      md: 'px-lg py-sm text-base',
      lg: 'px-xl py-md text-lg',
    };

    return (
      <button
        ref={ref}
        className={clsx(
          baseStyles,
          variants[variant],
          sizes[size],
          fullWidth && 'w-full',
          className
        )}
        disabled={disabled || isLoading}
        {...props}
      >
        {isLoading ? (
          <>
            <svg
              className="animate-spin -ml-1 mr-2 h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            Laden...
          </>
        ) : (
          children
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';
